import Select from "react-select";
import { useEffect } from "react";
import $api from "@/lib/queries/openapi";
import { useParams } from "react-router";
import { useFetchRfq } from "@/lib/queries/hooks";
import useGetShopSettings from "@/lib/queries/useGetShopSettings";
import useGetAccountingSystemOrganizations from "@/lib/queries/useGetAccountingSystemOrganizations";
import useGetOrganizationDetail from "@/lib/queries/useGetOrganizationDetail";
import useGetAccountingSystemOrganization from "@/lib/queries/useGetAccountingSystemOrganization";

import { useQueryClient } from "@tanstack/react-query";

export default function CustomerInfo({
  onValidChange,
}: {
  onValidChange: (valid: boolean) => void;
}) {
  const { teamSlug } = useParams();
  const rfq = useFetchRfq();
  const orgId = rfq?.contact?.organization?.id;

  const queryClient = useQueryClient();

  const shopQuery = useGetShopSettings(teamSlug);

  const hasAccountingSystem = !!shopQuery.data?.accounting_system_name;
  const orgQuery = useGetOrganizationDetail(
    teamSlug,
    orgId,
    hasAccountingSystem,
  );
  const orgDetail = orgQuery.data;

  const locked = !!orgDetail?.accounting_system_organization_id;
  const {
    data: accountingSystemOrgs,
    isLoading: accountingSystemOrgsLoading,
    error: accountingSystemOrgsError,
  } = useGetAccountingSystemOrganizations({
    teamSlug,
    enabled: !!orgDetail && hasAccountingSystem && !locked,
  });

  const { data: accountingOrg, error: accountingOrgError } =
    useGetAccountingSystemOrganization({
      teamSlug,
      accountingSystemOrganizationId:
        orgDetail?.accounting_system_organization_id ?? undefined,
      enabled: hasAccountingSystem && locked,
    });
  const { mutate: updateAccountingSystemOrg } = $api.useMutation(
    "patch",
    "/api/teams/{team_slug}/organizations/{id}",
  );

  useEffect(() => {
    const shopLoaded = !!shopQuery.data;

    if (!shopLoaded) return;

    if (hasAccountingSystem && !!orgDetail?.accounting_system_organization_id) {
      onValidChange(true);
    } else if (hasAccountingSystem) {
      onValidChange(false);
    } else {
      // No accounting system configured → implicitly valid
      onValidChange(true);
    }
  }, [shopQuery.data, orgDetail, hasAccountingSystem, onValidChange]);

  const accountingSystemOrgOptions = (accountingSystemOrgs ?? []).map(
    (org) => ({
      value: org.id,
      label: org.name,
    }),
  );

  const selectedOption = locked
    ? {
        value: orgDetail?.accounting_system_organization_id,
        label: accountingOrg?.name ?? "Loading...",
      }
    : null;

  type OptionType = { value: string | null; label: string };

  const handleSelect = (opt: OptionType | null) => {
    const selectedOrg = opt?.value ?? null;
    if (!selectedOrg || !orgId) return;

    updateAccountingSystemOrg(
      {
        params: {
          path: {
            team_slug: teamSlug!,
            id: orgId,
          },
        },
        body: {
          accounting_system_organization_id: selectedOrg,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [
              "get",
              "/api/teams/{team_slug}/organizations/{id}",
              {
                params: {
                  path: {
                    team_slug: teamSlug!,
                    id: orgId!,
                  },
                },
              },
            ],
          });
        },
      },
    );
  };

  return (
    <div className="p-4 flex flex-col gap-2">
      <div className="text-sm font-bold">Customer Information</div>
      <div className="text-sm">
        Contact: {rfq?.contact?.name} ({rfq?.contact?.email})
      </div>
      <div className="text-sm">
        Organization: {rfq?.contact?.organization?.name}
      </div>
      {hasAccountingSystem && (
        <div className="text-sm">
          {shopQuery.data?.accounting_system_name} Customer:{" "}
          {accountingSystemOrgsError || accountingOrgError ? (
            <div className="text-red-500">
              {accountingSystemOrgsError?.detail || accountingOrgError?.detail}
            </div>
          ) : locked ? (
            <span>{accountingOrg?.name ?? "Loading..."}</span>
          ) : (
            <div className="mt-1">
              <Select
                placeholder={`Select ${shopQuery.data?.accounting_system_name} customer`}
                options={accountingSystemOrgOptions}
                value={selectedOption}
                onChange={handleSelect}
                isClearable={false}
                isLoading={accountingSystemOrgsLoading}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
